import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Zap,
  BarChart3,
  ArrowLeft,
  CheckCircle2,
  WifiHigh,
  Battery,
  Info,
  Mail,
  MessageSquare,
  Phone,
  FileText,
  CreditCard,
  Droplets,
  Power,
  Play,
  Pause,
  CheckCircle,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs"; // Your custom URL-sync tabs component

import { Badge } from "@shared/ui/badge";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@shared/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@shared/hooks/use-toast";
import ApplicationHeader from "../ApplicationHeader";
import MainLayout from "../MainLayout";
import { useWorkflow } from "../WorkflowContext";
import WorkflowProgress from "../WorkflowProgress";
import {
  useActivateConsumer,
  useConsumerMeter,
  useUpdateStatus,
  useConsumerDetails,
} from "../../../hooks";
import { queryClient } from "@shared/api/queries/queryClient";

import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";

const communicationSchema = z.object({
  emailPreference: z.boolean().default(false),
  smsPreference: z.boolean().default(false),
  phonePreference: z.boolean().default(false),
  eBillPreference: z.boolean().default(false),
  paperBillPreference: z.boolean().default(false),
  meterConfigured: z.boolean().default(false),
  communicationMethod: z.boolean().default(false),
});

type CommunicationFormValues = z.infer<typeof communicationSchema>;

// Type for the activation payload
interface ActivateConsumerPayload {
  remark: string;
  additional_data: {
    is_meter_configured: boolean;
    communication: string[];
  };
}

const ActivationPage = () => {
  const {
    application,
    workflowSteps,
    moveToNextStep,
    updateApplicationStatus,
  } = useWorkflow();
  const navigate = useNavigate();
  const { consumerId } = useParams<{ consumerId: string }>();
  const { toast } = useToast();
  const [activationReady, setActivationReady] = useState(false);
  const activateConsumerMutation = useActivateConsumer();
  const remoteUtilityId = useRemoteUtilityId();
  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("New Service Connection Details");

  const { data: consumerDetailsData } = useConsumerDetails(
    {
      remote_utility_id: remoteUtilityId,
      consumer_id: consumerId || "",
      is_detail_info: true,
      is_document: false,
    },
    { enabled: !!consumerId && !!remoteUtilityId },
  );

  // Fetch consumer meter data
  const {
    data: consumerMeterData,
    isLoading: isLoadingConsumerMeter,
    refetch: refetchConsumerMeter,
  } = useConsumerMeter({
    consumer_id: consumerId || "",
    remote_utility_id: remoteUtilityId.toString(),
  });
  const updateStatus = useUpdateStatus(Number(consumerId));

  useEffect(() => {
    if (consumerId && remoteUtilityId) {
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerMeter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cx", "cxonboarding", "consumerMeter"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cx", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cxonboarding", "consumerDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["nsc", "consumerDetails"],
      });
      if (refetchConsumerMeter) {
        refetchConsumerMeter();
      }
    }
  }, [consumerId, remoteUtilityId, refetchConsumerMeter]);

  // Extract utility services and meter data from API response
  const utilityData = useMemo(() => {
    if (!consumerMeterData?.result?.consumerMappingData) return [];

    return consumerMeterData.result.consumerMappingData.map((mapping: any) => ({
      utilityService: mapping.utilityService, // Keep consistent naming
      meterDetails: mapping.meterDetails, // Keep consistent naming
      status: mapping.statusDisplay,
      isActive: mapping.isActive,
    }));
  }, [consumerMeterData]);

  const form = useForm<CommunicationFormValues>({
    resolver: zodResolver(communicationSchema),
    defaultValues: {
      emailPreference: false,
      smsPreference: false,
      phonePreference: false,
      eBillPreference: false,
      paperBillPreference: false,
      meterConfigured: false,
      communicationMethod: false,
    },
  });

  // Watch form values to update activation ready status
  const watchedValues = form.watch();

  useEffect(() => {
    startTracking();
    const hasRequiredCommunication =
      watchedValues.emailPreference ||
      watchedValues.smsPreference ||
      watchedValues.phonePreference ||
      watchedValues.eBillPreference ||
      watchedValues.paperBillPreference;

    const isReady =
      watchedValues.meterConfigured &&
      watchedValues.communicationMethod &&
      hasRequiredCommunication;

    setActivationReady(isReady);
  }, [watchedValues]);

  // Helper function to get meter unit based on utility service
  const getMeterUnit = (utilityService: string) => {
    switch (utilityService) {
      case "water":
        return "gal";
      case "waste water":
        return "gal";
      case "electric":
      case "electricity":
        return "kWh";
      case "gas":
        return "ccf";
      default:
        return "units";
    }
  };

  // Helper function to get appropriate icon for utility service
  const getUtilityIcon = (utilityService: string) => {
    switch (utilityService) {
      case "water":
        return <Droplets className="h-4 w-4" />;
      case "waste water":
        return <WifiHigh className="h-4 w-4" />;
      case "electric":
      case "electricity":
        return <Power className="h-4 w-4" />;
      default:
        return <CheckCircle2 className="h-4 w-4" />;
    }
  };

  const getUtilityIconSmall = (utilityService: string) => {
    switch (utilityService) {
      case "water":
      case "waste water":
        return <WifiHigh className="h-3 w-3 mr-1" />;
      case "electric":
      case "electricity":
        return <Battery className="h-3 w-3 mr-1" />;
      default:
        return <CheckCircle2 className="h-3 w-3 mr-1" />;
    }
  };

  if (!application) {
    return <div>Loading...</div>;
  }

  const handleActivate = async () => {
    try {
      // Build communication array based on selected preferences
      const communication = [];
      if (watchedValues.emailPreference) communication.push("email");
      if (watchedValues.smsPreference) communication.push("sms");
      if (watchedValues.phonePreference) communication.push("phone");
      if (watchedValues.eBillPreference) communication.push("ebill");
      if (watchedValues.paperBillPreference) communication.push("paper_bill");

      // Create payload
      const payload: ActivateConsumerPayload = {
        remark: "Service activation completed",
        additional_data: {
          is_meter_configured: watchedValues.meterConfigured,
          communication: communication,
        },
      };

      // Call API using the mutation hook
      await activateConsumerMutation.mutateAsync(
        {
          consumerId: consumerId || application.consumerId || application.id,
          payload: payload,
        },
        {
          onSuccess: () => {
            logTimeUntilStep("Activation");
            queryClient.refetchQueries({
              queryKey: ["cxonboarding", "consumerList"],
            });
          },
        },
      );

      // Update workflow state
      updateApplicationStatus("completed");
      moveToNextStep();

      toast({
        title: "Service activated",
        description: "The service has been successfully activated.",
      });

      navigate("/cx/consumer-management/");
    } catch (error) {
      toast({
        title: "Activation failed",
        description: "Failed to activate service. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    navigate(`/cx/consumer-onboarding/workflow/installation/${consumerId}`);
  };

  const handleAcknowledge = () => {
    // update the status in the database
    const acknowledge_date = new Date().toISOString();
    updateStatus.mutate({
      remote_utility_id: Number(remoteUtilityId),
      progress_status: "IN_PROGRESS",
      acknowledge_date,
    });
    toast({
      title: "Application acknowledged",
      description:
        "The application has been acknowledged and is now in progress.",
    });
  };

  const handlePutOnHold = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "HOLD",
      });

      toast({
        title: "Application on hold",
        description: "The application has been put on hold.",
      });
    } catch (error) {
      console.error("Error putting application on hold:", error);
      toast({
        title: "Error",
        description: "Failed to put application on hold. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResume = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "IN_PROGRESS",
      });

      toast({
        title: "Application resumed",
        description: "The application has been resumed and is now in progress.",
      });
    } catch (error) {
      console.error("Error resuming application:", error);
      toast({
        title: "Error",
        description: "Failed to resume application. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Define action buttons
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        application.progressStatusDisplay === "Acknowledge Pending",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        application.progressStatusDisplay === "In Progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => application.progressStatusDisplay === "Hold",
    },
  ];

  const relevantActions = actionButtons.filter(
    (action) =>
      !action.condition || action.condition(application.progressStatusDisplay),
  );

  function getStatusClasses(status) {
    switch (status) {
      case "Acknowledge Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "In Progress":
        return "bg-green-100 text-green-800 border-green-300";
      case "Hold":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/cx/consumer-onboarding")}
            className="hover:bg-gray-100 rounded-full h-10 w-10"
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-2xl font-bold">New Service Connection Details</h1>
        </div>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <WorkflowProgress
        steps={workflowSteps}
        currentStepId={application.currentStep}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Installation Status</CardTitle>
              <CardDescription>
                Track meter connection status by utility service
              </CardDescription>
            </CardHeader>

            <CardContent>
              {isLoadingConsumerMeter ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    Loading meter information...
                  </p>
                </div>
              ) : utilityData.length > 0 ||
                consumerDetailsData?.result?.additionalData?.nonMeterServices
                  ?.length > 0 ? (
                <div className="space-y-3">
                  {utilityData.map((utility: any, index: number) => {
                    const utilityServiceName = utility.utilityService?.replace(
                      /^_/,
                      "",
                    ); // Remove leading underscore
                    return (
                      <div
                        key={index}
                        className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm flex flex-col"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-base">
                              {utilityServiceName}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {utility.meterDetails
                                ? "Existing resource assigned to this service"
                                : "Awaiting installation action"}
                            </p>
                          </div>
                          <div
                            className={`whitespace-nowrap ml-2 font-medium text-xs px-3 py-1 rounded-full border ${
                              utility.meterDetails
                                ? "bg-green-50 text-green-600 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }`}
                          >
                            {utility.meterDetails
                              ? "Resource connected"
                              : "Pending"}
                          </div>
                        </div>

                        {utility.meterDetails && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="grid grid-cols-4 gap-4">
                              <div>
                                <p className="text-[11px] font-semibold text-gray-500  tracking-wider mb-1">
                                  Resource No
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                  {utility.meterDetails.meterNumber || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] font-semibold text-gray-500  tracking-wider mb-1">
                                  Serial No
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                  {utility.meterDetails.deviceNo || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] font-semibold text-gray-500  tracking-wider mb-1">
                                  Read Date
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                  {utility.meterDetails.currentReadingDate ||
                                    "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] font-semibold text-gray-500  tracking-wider mb-1">
                                  Current Read
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                  {utility.meterDetails.currentReading || "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {(
                    consumerDetailsData?.result?.additionalData
                      ?.nonMeterServices || []
                  ).map((service: string, index: number) => {
                    const utilityServiceName = service?.replace(/^_/, "");
                    return (
                      <div
                        key={`non-meter-${index}`}
                        className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm flex flex-col"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-base">
                              {utilityServiceName}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              No meter required for this service
                            </p>
                          </div>
                          <div className="whitespace-nowrap ml-2 font-medium text-xs px-3 py-1 rounded-full border bg-gray-100 text-gray-700 border-gray-200">
                            No meter connected
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    No meter information available
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="justify-between mt-4 p-0">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleActivate}
                disabled={
                  !activationReady ||
                  activateConsumerMutation.isPending ||
                  application.progressStatusDisplay === "Hold"
                }
              >
                {activateConsumerMutation.isPending
                  ? "Activating..."
                  : "Activate Service"}
                <Zap className="h-4 w-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-workflow-blue" />
                Activation Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Form {...form}>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="meterConfigured"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="">
                          <FormLabel className="font-medium text-sm">
                            Resource Configuration
                          </FormLabel>
                          <p className="text-xs text-muted-foreground">
                            Initial resource configurations complete
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="communicationMethod"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="">
                          <FormLabel className="font-medium text-sm">
                            Communication Method
                          </FormLabel>
                          <p className="text-xs text-muted-foreground">
                            At least one communication method selected
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />

                  <div className="pt-2 mt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        Activation Status
                      </span>
                      <Badge
                        className={
                          activationReady ? "bg-green-500" : "bg-amber-500"
                        }
                      >
                        {activationReady
                          ? "Ready for Activation"
                          : "Pending Requirements"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-workflow-blue" />
                Communication Preferences
              </CardTitle>
              <CardDescription className="">
                How would you like to receive communications about your service?
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-4">
              <Form {...form}>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="emailPreference"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="flex items-center cursor-pointer">
                          <Mail className="h-4 w-4 mr-2" />
                          Email
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="smsPreference"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="flex items-center cursor-pointer">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          SMS
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phonePreference"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="flex items-center cursor-pointer">
                          <Phone className="h-4 w-4 mr-2" />
                          Phone Call
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="eBillPreference"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="flex items-center cursor-pointer">
                          <CreditCard className="h-4 w-4 mr-2" />
                          E-Bill
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="paperBillPreference"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="flex items-center cursor-pointer">
                          <FileText className="h-4 w-4 mr-2" />
                          Paper Bill
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mt-4">
                    <p className="text-sm font-medium text-amber-800">
                      You must select at least one communication method to
                      proceed with activation.
                    </p>
                  </div>
                </div>
              </Form>
            </CardContent>
          </Card>

          {/* Meter Summary Card */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-workflow-blue" />
                Meter Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="">
              {isLoadingConsumerMeter ? (
                <p className="text-sm text-muted-foreground">
                  Loading meter data...
                </p>
              ) : utilityData.length > 0 ? (
                <div className="space-y-3">
                  {utilityData.map((utility: any, index: number) => (
                    <div
                      key={`${utility.utilityService}-${index}`}
                      className="p-3 rounded-lg border bg-blue-50 border-blue-100"
                    >
                      <h4 className="font-medium mb-2 flex items-center">
                        {getUtilityIcon(utility.utilityService)}
                        <span className="ml-2">
                          {utility.utilityService?.replace(/^_/, "")} Meter
                        </span>
                      </h4>
                      <div className="text-sm space-y-1">
                        <>
                          <p>
                            <span className="font-medium">Meter No:</span>{" "}
                            {utility?.meterDetails?.meterNumber}
                          </p>
                          <p>
                            <span className="font-medium">Device:</span>{" "}
                            {utility?.meterDetails?.deviceNo}
                          </p>
                          <p>
                            <span className="font-medium">Status:</span>
                            <span className="text-green-600 font-medium ml-1">
                              {utility?.meterDetails?.statusDisplay ||
                                utility?.status}
                            </span>
                          </p>
                        </>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No meters assigned
                </p>
              )}
            </CardContent>
          </Card> */}

          <TimelineTab
            remoteUtilityId={remoteUtilityId}
            entityId={consumerId}
            actionType={"nsc"}
          />
        </div>
      </div>
    </>
  );
};

export default ActivationPage;
